import React, { useEffect, useMemo } from 'react';
import Iconify from 'src/components/Iconify';
import { Autocomplete, Grid, IconButton, Stack, TextField } from '@mui/material';
import { i18nFunction } from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { TaskInput } from 'src/careflow/ph-core/fhir-client/resources';
import { ErrorFieldState } from './TaskModal';
import { GridSection } from 'src/components/CustomModal';
import { useCodeSystem } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';

type Props = {
  taskInput: TaskInput[] | null;
  updateState: Function;
  errors: ErrorFieldState;
  updateErrorState: Function;
  i18n: i18nFunction;
  configurations: any;
};

const TaskInputComponent = ({
  taskInput,
  errors,
  updateState,
  updateErrorState,
  i18n,
  configurations,
}: Props) => {
  const [taskInputTypeCS, { isFetching: isFetchingCS }] = useCodeSystem('ph-task-input-types');

  useEffect(() => {
    if (taskInput === undefined) {
      updateState({
        taskInputs: [{ type: {}, valueString: '' }],
      });
    }
  }, []);

  const handleAddInput = () => {
    updateState({
      taskInputs: [
        ...(taskInput ?? []),
        { type: {}, valueString: '' },
      ],
    });
  };

  const inputTypeCodeableConcept = useMemo(
    () =>
      taskInputTypeCS?.concept?.map((vs) => ({
        coding: [
          {
            code: vs?.code,
            display: vs?.display,
            system: `${configurations?.baseUrl}/fhir/CodeSystem/?name=ph-task-input-types`,
          },
        ],
      })) ?? [],
    [taskInputTypeCS, configurations]
  );

  const displayInputs = taskInput && Array.isArray(taskInput) && taskInput.length > 0 
    ? taskInput 
    : [{ type: {}, valueString: '' }];

  const getCurrentInputs = () => {
    return taskInput && Array.isArray(taskInput) && taskInput.length > 0 
      ? [...taskInput] 
      : [{ type: {}, valueString: '' }];
  };

  const canEditInput = checkAclValidation({ 
    acls: [
      crsAcls.CRS.PATIENT.TASKS.INPUT_OUTPUT.EDIT,
      crsAcls.CRS.CASE.TASKS.INPUT_OUTPUT.EDIT
    ] 
  });

  const canDeleteInput = checkAclValidation({ 
    acls: [
      crsAcls.CRS.PATIENT.TASKS.INPUT_OUTPUT.DELETE,
      crsAcls.CRS.CASE.TASKS.INPUT_OUTPUT.DELETE
    ] 
  });

  return (
    <Stack direction="column" mt={2} ml={2} mb={2}>
      {displayInputs.map((item: any, index) => {
        const inputValue = item?.valueString ?? item?.valueUrl ?? item?.valueReference?.reference ?? '';

        return (
          <GridSection container spacing={2} key={`input-${index}`} mt={index === 0 ? -2 : 0.4}>
            <Grid item xs={4}>
              <Autocomplete
                disabled={isFetchingCS || !canEditInput}
                value={item.type ?? undefined}
                fullWidth
                onChange={(_, newValue) => {
                  if (!canEditInput) return;
                  const currentInputs = getCurrentInputs();
                  currentInputs[index] = { ...currentInputs[index], type: newValue };
                  if (newValue) updateErrorState({ taskInputs: null });
                  updateState({ taskInputs: currentInputs });
                }}
                options={inputTypeCodeableConcept}
                getOptionLabel={(option: any) =>
                  option?.coding?.[0]?.display ??
                  option?.coding?.[0]?.code ??
                  ''
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={i18n('patients.details.tasks.inputType', 'crs')}
                    variant="outlined"
                    error={errors?.taskInputs?.includes(index)}
                    helperText={
                      errors?.taskInputs?.includes(index) &&
                      i18n('patients.details.tasks.inputTypeError', 'crs')
                    }
                  />
                )}
              />
            </Grid>

            <Grid item xs={6.6}>
              <TextField
                fullWidth
                disabled={isFetchingCS || !canEditInput}
                value={inputValue}
                onChange={(e) => {
                  if (!canEditInput) return;
                  const currentInputs = getCurrentInputs();
                  if (!currentInputs[index]) {
                    currentInputs[index] = { type: {}, valueString: '' };
                  }
                  currentInputs[index].valueString = e.target.value;
                  updateState({ taskInputs: currentInputs });
                }}
                label={i18n('patients.details.tasks.input', 'crs')}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={1.4}>
              <IconButton
                onClick={handleAddInput}
                size="medium"
                sx={{ '&:hover': { background: 'none' } }}
                disabled={isFetchingCS || !canEditInput}
              >
                <Iconify icon="eva:plus-fill" />
              </IconButton>

              <IconButton
                onClick={(e) => {
                  if (!canDeleteInput) return;
                  e.stopPropagation();
                  e.preventDefault();
                  const currentInputs = taskInput && Array.isArray(taskInput) && taskInput.length > 0 
                    ? [...taskInput] 
                    : [];
                  if (currentInputs.length === 0) return;
                  const updated = currentInputs.filter((_, i) => i !== index);
                  updateState({ taskInputs: updated.length > 0 ? updated : null });
                  if (errors?.taskInputs) {
                    const newErrors = errors.taskInputs.filter((_, i) => i !== index);
                    updateErrorState({
                      taskInputs: newErrors.length > 0 ? newErrors : null,
                    });
                  }
                }}
                size="medium"
                sx={{ '&:hover': { background: 'none' } }}
                disabled={isFetchingCS || !canDeleteInput}
              >
                <Iconify icon="eva:trash-2-outline" />
              </IconButton>
            </Grid>
          </GridSection>
        );
      })}
    </Stack>
  );
};

export default TaskInputComponent;
